"use client"

import { useEffect, useRef, useCallback, useMemo } from "react"
import { UseFormReturn } from "react-hook-form"

interface UseFormManagerOptions<T extends Record<string, any>> {
  formName: string
  recordId?: string | null
  form: UseFormReturn<T>
  defaultValues: T
  onDataRestored?: (data: any) => void
  extraData?: any
  externalClearers?: Array<(() => void) | undefined>
  onAfterReset?: () => void
  userId?: string | null
  disableCreateRestoration?: boolean
  /** When true, skip persistence if userId is missing (recommended for blog/event/product dashboards). */
  disableGuestRestoration?: boolean
  /** Auto-save debounce (ms). Default 900. */
  debounceMs?: number
}

/** Meaningful draft: titles, rich text, tags, images, location signals, event dates. */
function hasMeaningfulDraftSnapshot<T extends Record<string, any>>(values: T): boolean {
  const v = values as Record<string, unknown>
  const t = (s: unknown) => typeof s === "string" && s.trim().length > 0
  const richText = (s: unknown) =>
    typeof s === "string" && s.replace(/<[^>]*>/g, "").trim().length > 0

  if (t(v.title) || t(v.subTitle)) return true
  if (richText(v.content)) return true
  if (Array.isArray(v.tags) && v.tags.length > 0) return true
  if (Array.isArray(v.featureImg) && v.featureImg.length > 0) return true

  const loc = v.location as Record<string, unknown> | undefined
  if (loc && typeof loc === "object") {
    const addr = loc.address
    if (typeof addr === "string" && addr.trim().length > 0) return true
    const lat = loc.latitude
    const lng = loc.longitude
    if (typeof lat === "number" && lat !== 0) return true
    if (typeof lng === "number" && lng !== 0) return true
  }

  const period = v.eventPeriod as { startDate?: unknown; endDate?: unknown } | undefined
  if (period && (period.startDate || period.endDate)) return true

  return false
}

function hasMeaningfulExtraData(extraData: any): boolean {
  if (!extraData || typeof extraData !== "object") return false
  return Object.keys(extraData).some((k) => {
    const val = extraData[k]
    if (val == null) return false
    if (Array.isArray(val)) return val.length > 0
    if (typeof val === "object") return Object.keys(val as object).length > 0
    return !!val
  })
}

/**
 * Dashboard drafts. Blog/Event need broad draft detection + extraData; Job/Product share the same rules.
 */
export function useFormManager<T extends Record<string, any>>({
  formName,
  recordId,
  userId,
  form,
  defaultValues,
  onDataRestored,
  extraData,
  externalClearers = [],
  onAfterReset,
  disableCreateRestoration = false,
  disableGuestRestoration = false,
  debounceMs = 900,
}: UseFormManagerOptions<T>) {
  const { watch, reset, formState: { isDirty }, getValues } = form
  const isRestoring = useRef(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const instanceKey = useMemo(() => {
    return recordId ? `edit-${recordId}` : `create`
  }, [recordId])

  const storageKey = useMemo(() => {
    const prefix = userId ? `u-${userId}` : "guest"
    return `saas-form-${prefix}-${formName}-${instanceKey}`
  }, [userId, formName, instanceKey])

  const optionsRef = useRef({ onDataRestored, extraData, externalClearers, onAfterReset })
  useEffect(() => {
    optionsRef.current = { onDataRestored, extraData, externalClearers, onAfterReset }
  }, [onDataRestored, extraData, externalClearers, onAfterReset])

  const saveData = useCallback(
    (data: any) => {
      if (typeof window === "undefined" || isRestoring.current) return
      if (disableGuestRestoration && !userId) return
      try {
        localStorage.setItem(storageKey, JSON.stringify(data))
      } catch (e) {
        console.warn("FormManager: Failed to save", e)
      }
    },
    [storageKey, disableGuestRestoration, userId]
  )

  const flushSave = useCallback(() => {
    if (isRestoring.current) return
    if (disableGuestRestoration && !userId) return
    const currentData = getValues() as T
    const extra = optionsRef.current.extraData
    const merged = {
      ...currentData,
      ...((extra && typeof extra === "object" ? extra : {}) as object),
    }
    const shouldPersist =
      isDirty ||
      hasMeaningfulExtraData(extra) ||
      hasMeaningfulDraftSnapshot(currentData)

    if (!shouldPersist) return
    saveData(merged)
  }, [getValues, isDirty, saveData, disableGuestRestoration, userId])

  useEffect(() => {
    if (typeof window === "undefined") return
    if (disableGuestRestoration && !userId) return
    if (!recordId && disableCreateRestoration) return

    const savedData = localStorage.getItem(storageKey)
    if (!savedData) return

    try {
      const parsedData = JSON.parse(savedData)
      isRestoring.current = true
      reset(parsedData, { keepDefaultValues: true })
      if (optionsRef.current.onDataRestored) {
        optionsRef.current.onDataRestored(parsedData)
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isRestoring.current = false
        })
      })
    } catch (e) {
      console.warn("FormManager: Failed to restore", e)
      localStorage.removeItem(storageKey)
      isRestoring.current = false
    }
  }, [storageKey, reset, recordId, disableCreateRestoration, disableGuestRestoration, userId])

  const watchedValues = watch()
  useEffect(() => {
    if (disableGuestRestoration && !userId) return
    if (isRestoring.current) return

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      flushSave()
    }, debounceMs)

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [watchedValues, isDirty, flushSave, debounceMs, disableGuestRestoration, userId])

  useEffect(() => {
    if (disableGuestRestoration && !userId) return
    const onHidden = () => {
      if (document.visibilityState === "hidden") flushSave()
    }
    const onPageHide = () => flushSave()
    document.addEventListener("visibilitychange", onHidden)
    window.addEventListener("pagehide", onPageHide)
    return () => {
      document.removeEventListener("visibilitychange", onHidden)
      window.removeEventListener("pagehide", onPageHide)
    }
  }, [flushSave, disableGuestRestoration, userId])

  const resetFormCompletely = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(storageKey)
      } catch {
        /* ignore */
      }
    }
    isRestoring.current = true
    reset(defaultValues)
    optionsRef.current.externalClearers.forEach((clear) => clear?.())
    optionsRef.current.onAfterReset?.()
    queueMicrotask(() => {
      isRestoring.current = false
    })
  }, [storageKey, reset, defaultValues])

  const clearAfterSuccess = resetFormCompletely

  return {
    clearStorage: () => {
      if (typeof window === "undefined") return
      localStorage.removeItem(storageKey)
    },
    resetFormCompletely,
    clearAfterSuccess,
  }
}
